//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums } from "../../models/common/enums"
import { OpTariff } from "../../models/core/op-tariff"
import { OpDataTypeInGroup } from "../../models/core/op-data-type-in-group"

import { CommonHelper, ConvertHelper, LocalizationHelper, LogHelper, ToastHelper } from '../../helpers'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { DATA_TYPE_GROUP_SERVICE_NAME, DataTypeGroupService } from '../data-type'

import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'


// #region Consts

export const TARIFF_SERVICE_NAME: string = 'TariffService'
export const TARIFF_SERVICE_PROVIDER_NAME: string = 'TariffServiceProvider'

// #endregion


export class TariffService {

    // #region Consts

    public static get FILE_NAME_PREFIX(): string { return 'tariffs' }
    public static get TARIFF_SUMMARY_FILE_NAME_PREFIX(): string { return 'tariff-copy-summary' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdTariff }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Tariff }

    public static get LOG_PREFIX(): string { return '[SIMAX.Services.Tariff]' }

    // #endregion


    // #region Ctor

    constructor(
        private userSessionService: UserSessionService,
        private dataTypeGroupService: DataTypeGroupService
    ) {
    }

    // #endregion


    // #region Get

    public getTariff(idTariff: number): JQueryPromise<OpTariff> {
        return this.userSessionService
            .getDataProvider()
            .Tariff
            .getTariff(idTariff)
            .then((tariff: OpTariff) => {
                if (tariff != null) {
                    return tariff
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(TariffService.LOG_PREFIX, `Unable to load tariff for id ${idTariff}`, error)

                return null
            })
    }

    // #endregion


    // #region Delete

    public deleteTariff(idTariff: number, moduleName: Enums.SimaxModuleName): JQueryPromise<void> {
        const toDelete: OpTariff = new OpTariff(null)

        toDelete.idTariff = idTariff

        return this.userSessionService.getDataProvider()
            .Tariff
            .deleteTariff(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, moduleName))
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, moduleName))
                LogHelper.log(TariffService.LOG_PREFIX, `Unable to delete tariff. Error: ${errorMsg}.`)

                return $.Deferred<void>().reject(error).promise()
            })
    }

    // #endregion


    // #region Save

    public saveTariff(tariff: OpTariff, moduleName: Enums.SimaxModuleName, isBulkSave: boolean = false): JQueryPromise<OpTariff> {
        return this.userSessionService.getDataProvider()
            .Tariff
            .saveTariff(tariff)
            .then((savedObject: OpTariff) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.tariffSaved, true, 0)
                }
                return savedObject
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, moduleName))
                    console.warn(TariffService.LOG_PREFIX, `Unable to save tariff. Error: ${errorMsg}.`)
                }
                return $.Deferred<OpTariff>().reject(error).promise()
            })
    }

    public copyTariff(tariff: OpTariff, moduleName: Enums.SimaxModuleName): JQueryPromise<OpTariff> {
        return this.userSessionService.getDataProvider()
            .Tariff
            .copyTariff(tariff)
            .then((savedObject: OpTariff) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.tariffSaved, true, 0)
                return savedObject
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, moduleName))
                console.warn(TariffService.LOG_PREFIX, `Unable to copy tariff. Error: ${errorMsg}.`)
                return $.Deferred<OpTariff>().reject(error).promise()
            })
    }

    // #endregion


    // #region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: OpTariff = null, isCopy: boolean = false): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: TariffService.REFERENCE_TYPE,
            moduleName: TariffService.SIMAX_MODULE_NAME,
            referenceObject: referenceObject,
            referenceObjectId: referenceObjectId,
            sections: null,
            useBusinessObjects: true,
            isCopy: isCopy
        }
    }

    // #endregion


    // #region Parameters

    public getBasicParametersDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            TariffService.SIMAX_MODULE_NAME
        )
    }

    // #endregion
}

export class TariffServiceProvider implements ng.IServiceProvider {

    // #region IServiceProvider Implementation 

    public $get = [
        USER_SESSION_SERVICE_NAME,
        DATA_TYPE_GROUP_SERVICE_NAME,
        function (
            userSessionService: UserSessionService,
            dataTypeGroupService: DataTypeGroupService
        ) {
            return new TariffService(userSessionService, dataTypeGroupService)
        }]

    // #endregion
} 